<script lang="ts">
  import type { CompareBody } from '../../api/compare'
  import { buildTable, shortLabel } from '../../compare/table'
  import { colorFor } from '../../chart/palette'
  import { LEAD_WINDOWS } from '../../map/layers/modelLayer'
  import { models } from '../../state/models.svelte'
  import { formatInZone, HOUR, MINUTE, zoneLabel } from '../../time'

  /**
   * The same data as the meteogram, as numbers — and the chart's accessible
   * form. Since Stage 10.5 it is not a panel of its own: the Meteogram panel's
   * Chart / Table switch shows one or the other, under the same field chips
   * and the same window, so the two can never be looking at different things.
   *
   * **A dash is a real answer.** A model with no step at that instant has no
   * cell, and nothing is interpolated to fill one in: a 3-hourly GFS fills two
   * rows of every six. That is the rule the whole phase is built on.
   *
   * Rows are hours in the point's zone, broken by a rule and the weekday
   * where a local day starts, as round 2 draws it. Headers are short ("NBM",
   * "15m") because eight columns share 330 px; each one's accessible name is
   * the full label.
   */

  let {
    body,
    hidden,
    id = 'compare-table',
  }: { body: CompareBody; hidden: ReadonlySet<string>; id?: string } = $props()

  let now = $derived(Date.parse(body.generated))
  let timezone = $derived(body.point.timezone ?? null)

  /**
   * The same window the chart draws, forward only. The observed trace is a
   * measurement the chart already places where "this already happened" is
   * obvious, and the table's job is the forecast the models disagree about.
   */
  let window = $derived.by(() => {
    const ahead = LEAD_WINDOWS[models.window]
    return {
      from: now,
      to: Number.isFinite(ahead) ? now + ahead * MINUTE : now + 16 * 24 * HOUR,
    }
  })

  let table = $derived(buildTable(body.series, window, hidden))

  /** Cap the rows so a sixteen-day GFS is not 209 rows in a sheet. */
  const MAX_ROWS = 48

  let stamps = $derived(table.stamps.slice(0, MAX_ROWS))
  let truncated = $derived(table.stamps.length - stamps.length)

  /** The stamps, with a weekday row wherever the point's local date changes. */
  let rows = $derived.by(() => {
    const out: ({ kind: 'day'; label: string; key: string } | { kind: 'row'; at: number; index: number })[] = []
    let last = ''
    stamps.forEach((at, index) => {
      const date = formatInZone(new Date(at), timezone, { year: 'numeric', month: 'numeric', day: 'numeric' })
      if (last !== '' && date !== last) {
        out.push({ kind: 'day', label: formatInZone(new Date(at), timezone, { weekday: 'long' }), key: date })
      }
      last = date
      out.push({ kind: 'row', at, index })
    })
    return out
  })

  let zone = $derived(stamps.length > 0 ? zoneLabel(new Date(stamps[0]), timezone) : '')

  function fmt(value: number | null): string {
    if (value === null) return '–'
    const text = value.toFixed(body.field.decimals)
    return degrees ? `${text}°` : text
  }

  /** A temperature prints its degree sign, the way the chart's axis does. */
  let degrees = $derived(body.field.units.startsWith('°'))

  function hourLabel(stamp: number): string {
    const date = new Date(stamp)
    const sub = date.getUTCMinutes() !== 0
    return formatInZone(date, timezone, sub ? { hour: 'numeric', minute: '2-digit' } : { hour: 'numeric' })
  }

  function fullLabel(column: { kind: string; label: string }): string {
    return column.kind === 'blend' ? 'Mean of the models with a step, and how many' : column.label
  }
</script>

{#if table.columns.length === 0 || stamps.length === 0}
  <p class="muted empty">No model has a step inside this window.</p>
{:else}
  <table {id} style:--cols={table.columns.length}>
    <caption class="visually-hidden">
      {body.field.label} in {body.field.units} by model, for the next hours at this point.
      A dash means that model has no forecast step at that time.
    </caption>
    <colgroup>
      <col class="c-when" />
      {#each table.columns as column (column.id)}
        <col class:c-blend={column.kind === 'blend'} class:c-nws={column.kind === 'official'} />
      {/each}
    </colgroup>
    <thead>
      <tr>
        <th scope="col" class="when">{zone || 'Time'}</th>
        {#each table.columns as column (column.id)}
          <th scope="col" aria-label={fullLabel(column)} title={fullLabel(column)}>
            <span
              class="key"
              class:nws={column.kind === 'official'}
              style:background={column.kind === 'blend' ? 'transparent' : colorFor(column.kind, column.id)}
            ></span>
            <span class="short">{shortLabel(column)}</span>
          </th>
        {/each}
      </tr>
    </thead>
    <tbody>
      {#each rows as row (row.kind === 'day' ? `d${row.key}` : row.at)}
        {#if row.kind === 'day'}
          <tr class="day">
            <th scope="rowgroup" colspan={table.columns.length + 1} class="eyebrow">{row.label}</th>
          </tr>
        {:else}
          <tr>
            <th scope="row" class="when">{hourLabel(row.at)}</th>
            {#each table.columns as column (column.id)}
              {@const value = column.cells[row.index]}
              <td class:dash={value === null} class:strong={column.kind !== 'model'}>
                {fmt(value)}
                <!-- Every mean prints its own n: the membership changes hour
                     to hour as GFS drops to 3-hourly and NAM runs out, and
                     the mean of a different subset each hour is not a
                     consistent product. -->
                {#if column.counts && column.counts[row.index] !== null}
                  <span class="n">n {column.counts[row.index]}</span>
                {/if}
              </td>
            {/each}
          </tr>
        {/if}
      {/each}
    </tbody>
  </table>
  <p class="note">
    Times in {zone ? `${zone} (point's time)` : "the point's time"}. – means no step at that hour. Mean is the
    average of the models with a step, NBM excluded because it is already a blend; n counts them.
    The NWS forecast is not in it.
    {#if truncated > 0}{truncated} more step{truncated === 1 ? '' : 's'} in this window.{/if}
  </p>
{/if}

<style>
  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    font-size: var(--table-fs, 12px);
    font-variant-numeric: tabular-nums;
  }

  .c-when {
    width: 44px;
  }

  .c-nws {
    width: 38px;
  }

  .c-blend {
    width: 54px;
  }

  th,
  td {
    padding: 0;
    text-align: right;
    white-space: nowrap;
  }

  thead th {
    padding: 0 0 4px 3px;
    border-bottom: 2px solid var(--color-text);
    vertical-align: bottom;
    font-size: 10px;
    font-weight: 800;
  }

  thead th.when {
    text-align: left;
    padding-left: 0;
  }

  .key {
    display: block;
    height: 3px;
    margin-bottom: 4px;
  }

  .key.nws {
    height: 4px;
  }

  .short {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  tbody tr:not(.day) {
    border-bottom: 1px solid var(--color-divider);
  }

  tbody th,
  tbody td {
    line-height: var(--table-row, 22px);
  }

  tbody th.when {
    text-align: left;
    font-weight: 800;
  }

  td.dash {
    color: var(--color-muted);
  }

  td.strong {
    font-weight: 800;
  }

  .n {
    display: inline-block;
    width: 22px;
    margin-left: 4px;
    font-size: 10px;
    font-weight: 400;
    text-align: left;
    color: var(--color-muted);
  }

  tr.day th {
    padding: 8px 0 3px;
    border-bottom: 2px solid var(--color-text);
    text-align: left;
    line-height: 1.2;
  }

  .note {
    margin: 8px 0 0;
    font-size: 11px;
    line-height: 1.45;
    color: var(--color-muted);
  }

  .empty {
    font-size: 12px;
  }
</style>
